import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Clock, ArrowUpRight } from "lucide-react";
import { POSTS } from "@/data/posts";
import { useT } from "@/i18n/LanguageContext";
import SunDivider from "@/components/site/SunDivider";
import Seo from "@/components/site/Seo";
import { blogIndexSchema, breadcrumbSchema } from "@/lib/seo";

function formatDate(iso) {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export default function BlogIndexPage() {
  const { t } = useT();
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  const sorted = [...POSTS].sort(
    (a, b) => new Date(b.publishedAt) - new Date(a.publishedAt),
  );

  return (
    <article data-testid="blog-index-page">
      <Seo
        title="Insights · Dental Tips & Guides"
        description="Straight-talk dental tips from Drs. Yopa, Ide Noumy, and Ebrahimi at Smiles of Loudoun. Preventive care, cosmetic advice, and money-saving guides."
        path="/insights"
        jsonLd={[
          blogIndexSchema(sorted),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Insights", path: "/insights" },
          ]),
        ]}
      />

      {/* Hero */}
      <section className="px-6 md:px-10 lg:px-16 pt-14 md:pt-20 pb-10 md:pb-14 text-center">
        <div className="font-mono-eyebrow text-[var(--text-muted)]">
          {t("Insights · Smiles of Loudoun")}
        </div>
        <h1 className="font-serif-display text-[var(--primary)] mt-4 text-[48px] md:text-[80px] leading-[1] tracking-tight max-w-4xl mx-auto">
          {t("Smiles,")} <span className="italic text-[var(--accent)]">{t("explained")}</span>.
        </h1>
        <SunDivider className="mt-6" />
        <p className="mt-5 max-w-2xl mx-auto text-[var(--text-muted)] text-[17px]">
          {t("Small, honest dental tips from our team — preventive, cosmetic, and financial guidance in plain English.")}
        </p>
      </section>

      {/* Posts grid */}
      <section className="px-6 md:px-10 lg:px-16 pb-20 md:pb-28">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {sorted.map((post, i) => (
            <motion.div
              key={post.slug}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
            >
              <Link
                to={`/insights/${post.slug}`}
                data-testid={`blog-card-${post.slug}`}
                className="group block bg-[var(--surface)] border border-[var(--border)] rounded-3xl overflow-hidden hover:border-[var(--accent)] transition-colors"
              >
                <div className="aspect-[16/10] overflow-hidden bg-[var(--primary)]">
                  <img
                    src={post.cover}
                    alt={post.title}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.22em] text-[var(--text-muted)]">
                    <span>{formatDate(post.publishedAt)}</span>
                    <span>·</span>
                    <span className="inline-flex items-center gap-1">
                      <Clock className="w-3 h-3" strokeWidth={2} />
                      {post.readMinutes} {t("min read")}
                    </span>
                  </div>
                  <h2 className="font-serif-display text-[24px] leading-tight mt-3 text-[var(--primary)] group-hover:text-[var(--accent)] transition-colors">
                    {post.title}
                  </h2>
                  <p className="mt-3 text-[14.5px] text-[var(--text-muted)] line-clamp-3 leading-relaxed">
                    {post.excerpt}
                  </p>
                  <div className="mt-5 flex items-center justify-between">
                    <div className="text-xs text-[var(--text-muted)]">
                      {post.author}
                    </div>
                    <div className="inline-flex items-center gap-1 text-sm text-[var(--accent)]">
                      {t("Read")} <ArrowUpRight className="w-3.5 h-3.5" />
                    </div>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>
    </article>
  );
}
